import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReAuth } from '@/api/baseApi';
import { userTags } from '@/api/users/tags';
import { USERS_API } from '@/shared/api/constants';
import { PageableResponse } from '@/shared/api/types';
import { mapSortToQueryParameter } from '@/utils/utils';

import type {
  GetUsersPayload,
  UpdateUserPayload,
  UserRequest,
  UserResponse,
} from './types';

const normalizeOrganizationId = (
  organizationId?: string | string[],
): string | undefined => {
  if (Array.isArray(organizationId)) {
    return [...organizationId].pop();
  }
  return organizationId;
};

const normalizeFilters = (filters: any) => {
  if (filters?.organizationId) {
    return {
      ...filters,
      organizationId: normalizeOrganizationId(filters?.organizationId),
    };
  }
  return filters;
};

export const usersApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(userTags),
  endpoints: (builder) => ({
    getUsers: builder.query<PageableResponse<UserResponse>, GetUsersPayload>({
      query: (payload) => ({
        url: USERS_API,
        method: 'GET',
        params: {
          ...normalizeFilters(payload?.filters),
          page: payload?.pageable?.page,
          size: payload?.pageable?.size,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [userTags.USERS],
    }),

    getUserById: builder.query<UserResponse, string>({
      query: (id) => ({
        url: `${USERS_API}/${id}`,
        method: 'GET',
      }),
      providesTags: [userTags.USER_RECORD],
    }),

    createUser: builder.mutation<UserResponse, UserRequest>({
      query: (payload) => ({
        url: USERS_API,
        method: 'POST',
        body: {
          ...payload,
          organizationId: normalizeOrganizationId(payload.organizationId),
        },
      }),
      invalidatesTags: [userTags.USERS],
    }),

    updateUser: builder.mutation<UserResponse, UpdateUserPayload>({
      query: ({ id, ...payload }) => ({
        url: `${USERS_API}/${id}`,
        method: 'PUT',
        body: {
          ...payload,
          organizationId: normalizeOrganizationId(payload.organizationId),
        },
      }),
      invalidatesTags: [userTags.USERS, userTags.USER_RECORD],
    }),

    blockUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `${USERS_API}/${id}/block`,
        method: 'POST',
      }),
      invalidatesTags: [userTags.USERS, userTags.USER_RECORD],
    }),

    unblockUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `${USERS_API}/${id}/unblock`,
        method: 'POST',
      }),
      invalidatesTags: [userTags.USERS, userTags.USER_RECORD],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
} = usersApi;
